/**
 *service
 */
import taskmanAxios from '../helpers/axios'
import { extractTaskList } from '../utils/taskList'
import TaskService from "./task.service";
/**
 * const
 */
import {
    API_MAKE_PROJECT_GET_URL,
    API_PROJECT_CREATE_URL, API_PROJECT_LIST_URL,
    API_PROJECT_STATUS_PRESETS_URL,
    API_PROJECT_WIDGETS_URL,
    API_PROJECT_MY_TASKS_WIDGET_URL,
    API_MAKE_PROJECT_TASK_STATUSES_URL,
    API_MAKE_PROJECT_TASK_STATUS_URL,
    API_MAKE_PROJECTS_ORDER_URL,
    API_MAKE_PROJECT_UPDATE_URL,
    API_MAKE_PROJECT_DELETE_URL,
    API_MAKE_PROJECT_ARCHIVE_URL,
    API_MAKE_PROJECT_UNARCHIVE_URL,
    API_MAKE_PROJECT_TASKS_URL, API_PROJECT_STATISTICS_URL, API_PROJECT_WORK_SPACE_TASK_URL,
} from "../constants/api.constant";
import {ProjectUpdate} from "../types";
import {
    ProjectTaskStatusItem,
    sortProjectStatuses,
    toProjectBoardStatuses,
    toProjectStatusPayload,
} from "../utils/projectStatus";

export default class ProjectService {
    private static projectStatusesPromiseCache = new Map<string, Promise<ProjectTaskStatusItem[]>>()

    private static invalidateProjectStatusCache(projectId: string | number) {
        this.projectStatusesPromiseCache.delete(String(projectId))
    }

    private static stringifyWidgetParams(params: Record<string, any>) {
        const searchParams = new URLSearchParams()

        Object.entries(params || {}).forEach(([key, value]) => {
            if (value == null) return

            if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, String(item)))
                return
            }

            searchParams.append(key, String(value))
        })

        return searchParams.toString()
    }

    private static attachRelatedTasks(taskList: any[]) {
        const flattenedTasks = [...taskList]
        const seenIds = new Set(taskList.filter((task) => task?.id != null).map((task) => String(task.id)))
        for (let index = 0; index < flattenedTasks.length; index += 1) {
            const parent = flattenedTasks[index]
            for (const child of (Array.isArray(parent?.relatedTasks) ? parent.relatedTasks : [])) {
                if (child?.id == null || seenIds.has(String(child.id))) continue
                seenIds.add(String(child.id))
                flattenedTasks.push({ ...child, parent_id: child.parent_id ?? parent.id })
            }
        }

        const tasks = flattenedTasks.map((task) => {
            if (!Array.isArray(task?.relatedTasks)) {
                return task
            }

            return {
                ...task,
                relatedTasks: task.relatedTasks.filter(Boolean),
            }
        })
        const taskById = new Map(
            tasks
                .filter((task) => task?.id != null)
                .map((task) => [String(task.id), task]),
        )

        tasks.forEach((task) => {
            if (task?.parent_id == null) return

            const parentTask = taskById.get(String(task.parent_id))
            if (!parentTask) return

            const relatedTasks = Array.isArray(parentTask.relatedTasks)
                ? parentTask.relatedTasks
                : []
            const alreadyAttached = relatedTasks.some((relatedTask: any) => (
                relatedTask?.id != null && String(relatedTask.id) === String(task.id)
            ))

            if (!alreadyAttached) {
                parentTask.relatedTasks = [...relatedTasks, task]
            }
        })

        return tasks.map((task) => Array.isArray(task?.relatedTasks)
            ? { ...task, relatedTasks: [...task.relatedTasks, null] }
            : task)
    }

    static async createWorkspace(data: {
        name: string;
        is_public: boolean;
        initial_members: [];
    }) {

        // 3 id


        // return await taskmanAxios.post("/workspaces/", data)
        //     .then(response => response.data)
        //     .catch(e => Promise.reject(e));
    }


    static async create(data: ProjectUpdate){
        return await taskmanAxios.post(API_PROJECT_CREATE_URL, data)
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async list(params?: Record<string, any>){
        return await taskmanAxios.get(API_PROJECT_LIST_URL, { params })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async listArchived(){
        return await this.list({
            skip: 0,
            limit: 100,
            archived_only: true,
            mine_only: false,
            trash_only: false,
        })
    }

    static async reorder(workspaceId: string | number, projectIds: number[], archivedOnly = false){
        return await taskmanAxios.put(
            API_MAKE_PROJECTS_ORDER_URL(workspaceId),
            { project_ids: projectIds },
            { params: { archived_only: archivedOnly } },
        )
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static statusPresetsPromise: Promise<any> | null = null

    static async getStatusPresets(){
        // Presets are static — cache the request so reopening the status modal
        // does not refetch them every time.
        if (!this.statusPresetsPromise) {
            this.statusPresetsPromise = taskmanAxios.get(API_PROJECT_STATUS_PRESETS_URL)
                .then(response => response.data)
                .catch(e => {
                    this.statusPresetsPromise = null
                    return Promise.reject(e)
                });
        }

        return this.statusPresetsPromise
    }

    static async getWidgets(){
        return await taskmanAxios.get(API_PROJECT_WIDGETS_URL)
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getMyTasksWidget(params?: Record<string, any>){
        return await taskmanAxios.get(API_PROJECT_MY_TASKS_WIDGET_URL, { params })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async get(projectId: string){
        return await taskmanAxios.get(API_MAKE_PROJECT_GET_URL(projectId))
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getProjectWithStatuses(project: any){
        if (project?.id == null) {
            return project
        }

        if (
            (Array.isArray(project.task_statuses) && project.task_statuses.length)
            || (Array.isArray(project.statuses) && project.statuses.length)
        ) {
            return project
        }

        const statuses = await this.getCachedProjectStatuses(project.id)
        return {
            ...project,
            statuses: toProjectBoardStatuses(statuses),
        }
    }

    static async getTaskStatuses(projectId: string){
        return await taskmanAxios.get(API_MAKE_PROJECT_TASK_STATUSES_URL(projectId))
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getSortedTaskStatuses(projectId: string): Promise<ProjectTaskStatusItem[]>{
        const statusData = await this.getTaskStatuses(projectId)
        const raw: ProjectTaskStatusItem[] = statusData.statuses || statusData || []
        return sortProjectStatuses(raw)
    }

    static async getCachedProjectStatuses(projectId: string | number): Promise<ProjectTaskStatusItem[]>{
        const cacheKey = String(projectId)
        const cachedRequest = this.projectStatusesPromiseCache.get(cacheKey)

        if (cachedRequest) {
            return cachedRequest
        }

        const request = this.getSortedTaskStatuses(cacheKey).catch((error) => {
            this.projectStatusesPromiseCache.delete(cacheKey)
            return Promise.reject(error)
        })
        this.projectStatusesPromiseCache.set(cacheKey, request)
        return request
    }

    static async updateTaskStatuses(projectId: string, statuses: { key: string; name: string; color: string; position: number }[]){
        return await taskmanAxios.put(API_MAKE_PROJECT_TASK_STATUSES_URL(projectId), statuses)
            .then(response => {
                this.invalidateProjectStatusCache(projectId)
                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async createTaskStatus(projectId: string, data: { key: string; name: string; color: string; position: number }){
        return await taskmanAxios.post(API_MAKE_PROJECT_TASK_STATUSES_URL(projectId), data)
            .then(response => {
                this.invalidateProjectStatusCache(projectId)
                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async updateTaskStatus(projectId: string, statusId: string | number, data: { name?: string; color?: string; position?: number }){
        return await taskmanAxios.patch(API_MAKE_PROJECT_TASK_STATUS_URL(projectId, statusId), data)
            .then(response => {
                this.invalidateProjectStatusCache(projectId)
                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async deleteTaskStatus(projectId: string, statusId: string | number){
        return await taskmanAxios.delete(API_MAKE_PROJECT_TASK_STATUS_URL(projectId, statusId))
            .then(response => {
                this.invalidateProjectStatusCache(projectId)
                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async update(projectId: string, data: any){
        return await taskmanAxios.put(API_MAKE_PROJECT_UPDATE_URL(projectId), data)
            .then(response => {
                this.invalidateProjectStatusCache(projectId)
                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async delete(projectId: string){
        return await taskmanAxios.delete(API_MAKE_PROJECT_DELETE_URL(projectId))
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async archive(projectId: string | number){
        return await taskmanAxios.post(API_MAKE_PROJECT_ARCHIVE_URL(projectId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async unarchive(projectId: string | number){
        return await taskmanAxios.post(API_MAKE_PROJECT_UNARCHIVE_URL(projectId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getProjectTasks(projectId: string, params?: Record<string, any>, options?: { attachRelatedTasks?: boolean }){
        return await taskmanAxios.get(API_MAKE_PROJECT_TASKS_URL(projectId), { params })
            .then(response => {
                if (options?.attachRelatedTasks !== false) {
                    response.data.tasks = this.attachRelatedTasks(response.data.tasks)
                }

                return response.data
            })
            .catch(e => Promise.reject(e));
    }

    static async getProjectStatistic(params?: Record<string, any>){
        return await taskmanAxios.get(API_PROJECT_STATISTICS_URL, {
            params,
            paramsSerializer: params => this.stringifyWidgetParams(params),
        })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getProjectWorkSpaceTasks(params?: Record<string, any>){
        return await taskmanAxios.get(API_PROJECT_WORK_SPACE_TASK_URL, {
            params,
            paramsSerializer: params => {
                return this.stringifyWidgetParams(params);
            },
        })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async getAllProjectTasks(projectId: string, params?: Record<string, any>){
        const tasks: any[] = []
        const seenTaskIds = new Set<string>()
        let skip = 0
        let total: number | null = null

        while (true) {
            const response = await this.getProjectTasks(projectId, {
                ...params,
                skip,
            }, { attachRelatedTasks: false })
            const pageTasks = extractTaskList(response)
            const responseTotal = Number(response?.total ?? response?.data?.total)

            if (Number.isFinite(responseTotal) && responseTotal >= 0) {
                total = responseTotal
            }

            let addedTasks = 0
            pageTasks.forEach((task: any, index: number) => {
                const taskKey = task?.id == null ? `page-${skip}-item-${index}` : String(task.id)
                if (seenTaskIds.has(taskKey)) return

                seenTaskIds.add(taskKey)
                tasks.push(task)
                addedTasks += 1
            })

            if (
                pageTasks.length === 0
                || addedTasks === 0
            ) {
                break
            }

            skip += pageTasks.length
        }

        const attachedTasks = this.attachRelatedTasks(tasks)
        return {
            tasks: attachedTasks,
            total: Math.max(total ?? 0, attachedTasks.length),
        }
    }

    static async getAllProjectWorkSpaceTasks(params?: Record<string, any>){
        const pageSize = 200
        const tasks: any[] = []
        const seenTaskIds = new Set<string>()
        let skip = 0
        let total: number | null = null

        while (total == null || skip < total) {
            const response = await this.getProjectWorkSpaceTasks({
                ...params,
                skip,
                limit: pageSize,
            })
            const pageTasks = extractTaskList(response)
            const responseTotal = Number(response?.total ?? response?.data?.total)

            if (Number.isFinite(responseTotal) && responseTotal >= 0) {
                total = responseTotal
            }

            let addedTasks = 0
            pageTasks.forEach((task: any, index: number) => {
                const taskKey = task?.id == null ? `page-${skip}-item-${index}` : String(task.id)
                if (seenTaskIds.has(taskKey)) return

                seenTaskIds.add(taskKey)
                tasks.push(task)
                addedTasks += 1
            })

            if (
                pageTasks.length === 0
                || addedTasks === 0
                || (total != null && skip + pageTasks.length >= total)
                || (total == null && pageTasks.length < pageSize)
            ) {
                break
            }

            skip += pageTasks.length
        }

        return {
            tasks: this.attachRelatedTasks(tasks),
            total: total ?? tasks.length,
        }
    }

    static async listArchivedTasks(projectId: string) {
        return this.getAllProjectTasks(projectId, {
            archived_only: true,
            mine_only: false,
            trash_only: false,
        }).then((response) => extractTaskList(response))
    }

    static async duplicateProject(project: {
        id: string | number;
        description?: string;
        workspace_id: number;
        is_public?: boolean;
        color: string;
    }, name: string, workspaceId?: number, options: { copyContent?: boolean; color?: string } = {}){
        const projectId = String(project.id)
        const copyContent = options.copyContent ?? true
        let task_statuses: { key: string; name: string; color: string; position: number }[] = []

        if (copyContent) {
            try {
                const currentStatuses = await this.getSortedTaskStatuses(projectId)
                task_statuses = toProjectStatusPayload(currentStatuses)
            } catch (e) {
                console.error('Failed to fetch statuses for duplication:', e)
            }
        }

        const newProject = await this.create({
            name,
            description: copyContent ? project.description || '' : '',
            workspace_id: workspaceId ?? project.workspace_id,
            is_public: copyContent ? project.is_public ?? true : true,
            color: options.color || project.color,
            ...(copyContent && task_statuses.length ? { task_statuses } : {}),
        })

        if (!copyContent) {
            return newProject
        }

        try {
            const tasksData = await this.getProjectTasks(projectId)
            const projectTasks = tasksData.tasks || tasksData || []

            await Promise.all(projectTasks.map((task: any) => (
                TaskService.create({
                    title: task.title,
                    description: task.description || '',
                    status: task.status,
                    priority: task.priority || 'medium',
                    task_type: task.task_type || 'task',
                    assignee_ids: Array.isArray(task.assignee_ids) ? task.assignee_ids : [],
                    due_date: task.due_date || null,
                    project_id: newProject.id,
                })
            )))
        } catch (e) {
            console.error('Failed to copy tasks:', e)
        }

        return newProject
    }

}
